import Styles from './styles.module.css';
import '../../../auth/login/tokens.css';
import React, { useState } from 'react';
import { GoCheck, GoChevronDown, GoX } from 'react-icons/go';
import { useAppSelector } from 'src/salon/app/hook';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

type PlanKey = '無料' | '有料';

// カード本体は「要点だけ」。細かい ○ / × は下の比較表（アコーディオン）に逃がす。
// 機能名と ○ / × はサービス資料 p.17「料金」に準拠。
const FREE_POINTS = [
  '10学校を選択して送付',
  '求人票の公開',
  'サロン8タイプ分類',
];

const PAID_POINTS = [
  '210学校すべてに送付',
  '求人ページの公開',
  'ワンタップサロン見学',
  'アプリ応募',
  'マッチ率',
  'スタイル写真検索',
];

const COMPARISON: { label: string; free: boolean; paid: boolean }[] = [
  { label: '10学校を選択して送付', free: true, paid: true },
  { label: '210学校すべてに送付', free: false, paid: true },
  { label: '求人票の公開', free: true, paid: true },
  { label: '求人ページの公開', free: false, paid: true },
  { label: 'ワンタップサロン見学', free: false, paid: true },
  { label: 'アプリ応募', free: false, paid: true },
  { label: 'マッチ率', free: false, paid: true },
  { label: 'サロン8タイプ分類', free: true, paid: true },
  { label: 'スタイル写真検索', free: false, paid: true },
];

const ManualPlan: React.FC = () => {

  const [planSelect, setPlanSelect] = useState<PlanKey | ''>('');
  const [showCompare, setShowCompare] = useState<boolean>(false);

  const company = useAppSelector((state) => state.company.company);
  const navigate = useNavigate();

  // -------------------------
  // 更新処理（Firestore → 次ステップへ）
  // -------------------------

  async function update() {
    if (!company.id) return;

    const db = getFirestore();
    const ref = doc(db, 'companies', company.id);

    await updateDoc(ref, {
      'progress.plan': true,
      'contract': planSelect === '有料',
    });

    navigate('../icon');
  }

  return (
    <div className={Styles.page}>
      <div className={Styles.inner}>

        <p className={Styles.kicker}>STEP 1</p>
        <h1 className={Styles.title}>プランを選択</h1>
        <p className={Styles.lead}>
          まずは無料から始められます。必要に応じて採用活動を拡張できます。
        </p>
        <p className={Styles.leadNote}>
          ※有料プランをご選択された場合は料金のお支払いが発生しますので、ご確認の上ご選択ください
        </p>

        <div className={Styles.cards}>

          {/* 無料プラン */}
          <section className={Styles.card}>
            <span className={Styles.chip}>FREE</span>
            <h2 className={Styles.planName}>無料プラン</h2>
            <p className={Styles.price}>¥0<span>/ 年</span></p>
            <p className={Styles.desc}>
              地域を絞って求人票を届けられます。10校までの送付と、アプリでの求人票公開ができます。
            </p>

            <button type='button' className={Styles.selectGhost} onClick={() => setPlanSelect('無料')}>
              このプランを選択
            </button>

            <ul className={Styles.points}>
              {FREE_POINTS.map((p) => (
                <li key={p}><GoCheck className={Styles.check} />{p}</li>
              ))}
            </ul>
          </section>

          {/* 有料プラン */}
          <section className={`${Styles.card} ${Styles.cardPaid}`}>
            <span className={`${Styles.chip} ${Styles.chipPaid}`}>STANDARD</span>
            <h2 className={Styles.planName}>有料プラン</h2>
            <p className={Styles.price}>¥100,000<span>/ 年</span></p>
            <p className={Styles.desc}>
              全国210校すべてに送付。求人ページ・サロン見学・アプリ応募まで、採用活動を全面的に広げられます。
            </p>

            <button type='button' className={Styles.selectPrimary} onClick={() => setPlanSelect('有料')}>
              このプランを選択
            </button>

            <p className={Styles.pointsLead}><GoCheck className={Styles.check} />無料プランの内容</p>
            <ul className={Styles.points}>
              {PAID_POINTS.map((p) => (
                <li key={p}><GoCheck className={Styles.check} />{p}</li>
              ))}
            </ul>
          </section>

        </div>

        {/* 詳細な機能比較（アコーディオン） */}
        <div className={Styles.compare}>
          <button
            type='button'
            className={Styles.compareToggle}
            onClick={() => setShowCompare((v) => !v)}
            aria-expanded={showCompare}
          >
            詳細な機能比較を見る
            <GoChevronDown
              className={`${Styles.compareIcon} ${showCompare ? Styles.compareIconOpen : ''}`}
            />
          </button>

          {showCompare && (
            <div className={Styles.compareTableWrap}>
              <table className={Styles.compareTable}>
                <thead>
                  <tr>
                    <th>機能</th>
                    <th>無料</th>
                    <th className={Styles.compareColPaid}>有料</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((row) => (
                    <tr key={row.label}>
                      <th scope='row'>{row.label}</th>
                      <td>{renderCell(row.free)}</td>
                      <td className={Styles.compareColPaid}>{renderCell(row.paid)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {planSelect !== '' && (
        <div className={Styles.modalOverlay} role='dialog' aria-modal='true'>
          <div className={Styles.modalScrim} onClick={() => setPlanSelect('')} />
          <div className={Styles.modalCard}>
            <h3 className={Styles.modalTitle}>{planSelect}プランでお間違いないでしょうか？</h3>
            <p className={Styles.modalText}>
              契約プランの状況が曖昧な場合は、営業担当者にご確認くださいませ。
            </p>
            {planSelect === '有料' && (
              <p className={Styles.modalNote}>
                有料プランをご選択いただく場合は、年間プランのお支払いが必要となりますので、ご確認の上、お進みくださいませ。
              </p>
            )}
            <div className={Styles.modalActions}>
              <button type='button' className={Styles.modalCancel} onClick={() => setPlanSelect('')}>
                戻る
              </button>
              <button type='button' className={Styles.modalConfirm} onClick={() => update()}>
                確定する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function renderCell(value: boolean) {
  return value
    ? <GoCheck className={Styles.cellCheck} aria-label='対応' />
    : <GoX className={Styles.cellCross} aria-label='非対応' />;
}

export default ManualPlan;
